'use client';

import ControlledButton from '@umodeapporg/ui/components/Form/ControlledButton';
import ControlledTooltip from '@umodeapporg/ui/components/UI/ControlledTooltip';
import { MaterialSymbol } from '@umodeapporg/ui/components/UI/MaterialSymbol';
import { useRouter } from 'next/navigation';
import { useTranslation } from 'react-i18next';

import { formatDiaLongo, formatHoras } from '@/app/relatorios/helpers/formatRelatorios';
import { DIA_KEYS } from '@/domain/configuracao/helpers/jornada';
import { cn } from '@/lib/utils';

import { ICelulaDaVisao } from '../helpers/visaoExecutiva';
import { MAPA_POR_SEMANA_HREF, mapaDaCelulaHref } from '../helpers/visaoExecutivaLinks';
import { BlocoCard } from './BlocoCard';
import { EstadoDoBloco } from './EstadoDoBloco';

/** Transparent at rest, like the rows of the sibling cards: the hover is what says it is clickable. */
const LINHA = 'flex w-full flex-col gap-1 rounded-md px-2 py-1.5 text-left';

interface OciosidadeBlocoProps {
  celulas: Array<ICelulaDaVisao>;
  loading: boolean;
  failed: boolean;
  onRetry: () => void;
  className?: string;
}

/**
 * Quando cada célula fica livre: o momento em que a última OP que o motor programou nela
 * termina. É a partir dele que o comercial pode vender sem mexer na carteira de hoje.
 *
 * O motor programa em minutos corridos (a OP seguinte começa no minuto em que a anterior
 * acaba) e agora entrega também a HORA do fim: a célula que fecha a última OP às 14:30
 * fica livre às 14:30 daquele dia, e não só no dia útil seguinte. Acabando no fim do
 * expediente, a resposta continua sendo o próximo dia útil.
 *
 * Clicar numa célula abre o Mapa › Por semana filtrado nela, com o detalhe já na semana em
 * que ela fica livre: é lá que se vê o que ainda ocupa a célula até aquele momento.
 */
export function OciosidadeBloco({ celulas, loading, failed, onRetry, className }: OciosidadeBlocoProps) {
  const { t } = useTranslation('common');
  const router = useRouter();

  /**
   * A data com o dia da semana ("Qui 11/02/2027"). Quem vende precisa saber que dia da
   * semana é aquilo sem ir ao calendário, e o ISO é lido em UTC para o fuso de quem
   * olha nunca empurrar a data um dia.
   */
  const dataComDiaDaSemana = (dia: string, hora: string | null): string => {
    const data = new Date(`${dia}T00:00:00.000Z`);
    if (Number.isNaN(data.getTime())) {
      return formatDiaLongo(dia);
    }
    const escrita = t('visaoExecutiva.ociosidade.dataLivre', {
      semana: t(`configuracao.semana.diasCurtos.${DIA_KEYS[data.getUTCDay()]}`),
      data: formatDiaLongo(dia),
    });
    // A HORA só aparece quando a célula esvazia no meio do dia: o motor programa em
    // minutos corridos e agora entrega a hora do fim (N45). Livre o dia inteiro, a
    // data sozinha já é a resposta.
    return hora === null ? escrita : t('visaoExecutiva.ociosidade.dataLivreHora', { data: escrita, hora });
  };

  return (
    <BlocoCard
      pergunta={t('visaoExecutiva.ociosidade.pergunta')}
      fonte={t('visaoExecutiva.ociosidade.fonte')}
      acao={{ label: t('visaoExecutiva.ociosidade.abrir'), href: MAPA_POR_SEMANA_HREF }}
      className={className}
    >
      <EstadoDoBloco
        loading={loading}
        failed={failed}
        vazio={celulas.length === 0}
        vazioTexto={t('visaoExecutiva.ociosidade.vazio')}
        onRetry={onRetry}
      >
        <ul className="flex animate-fade-in flex-col gap-2">
          {celulas.map((celula) => (
            <li key={celula.celula} className="flex min-w-0">
              <ControlledTooltip content={t('visaoExecutiva.ociosidade.irParaCelula', { celula: celula.celula })}>
                <ControlledButton
                  defaultHtmlButton
                  type="button"
                  onClick={() => router.push(mapaDaCelulaHref(celula.celula, celula.livreApartirDe))}
                  className={cn(LINHA, 'interactive hover:bg-muted')}
                >
                  <div className="flex w-full items-baseline justify-between gap-2">
                    <span className="truncate text-xs font-semibold text-foreground">{celula.celula}</span>
                    {/* Célula sem nenhuma OP programada já está livre: uma data aqui inventaria
                      uma espera que não existe. */}
                    <span className="shrink-0 text-sm font-semibold tabular-nums text-foreground">
                      {celula.livreApartirDe === null
                        ? t('visaoExecutiva.ociosidade.jaLivre')
                        : dataComDiaDaSemana(celula.livreApartirDe, celula.livreApartirDeHora)}
                    </span>
                  </div>
                  <span className="text-xs tabular-nums text-foreground-subtle">
                    {t('visaoExecutiva.ociosidade.horasLivres', { horas: formatHoras(celula.horasLivresAteLa) })}
                  </span>
                </ControlledButton>
              </ControlledTooltip>
            </li>
          ))}
        </ul>

        {/* A leitura comercial do bloco, escrita: sem ela a tabela de datas não diz o que
            fazer com elas. */}
        <p className="mt-auto flex items-start gap-2 text-xs text-foreground-muted">
          <MaterialSymbol icon="sell" size={16} className="shrink-0 text-brand-emphasis" aria-hidden />
          {t('visaoExecutiva.ociosidade.comercial')}
        </p>
      </EstadoDoBloco>
    </BlocoCard>
  );
}
